package com.swetdot.swetgram.data.media

import coil3.ImageLoader
import coil3.PlatformContext
import coil3.annotation.ExperimentalCoilApi
import coil3.network.ktor3.KtorNetworkFetcherFactory
import io.ktor.client.HttpClient

/**
 * The app's one [ImageLoader].
 *
 * Built here rather than left to Coil's default for a single reason: the default
 * `KtorNetworkFetcherFactory()` constructs an `HttpClient()` of its own, with whatever engine
 * happens to be on the classpath. That is a second connection pool, a second set of defaults, and
 * on iOS it is a client built with no explicit engine at all. Handing it the app's engine instead
 * means image GETs share the pool every other request already uses.
 *
 * [http] must be the client from
 * [createMediaHttpClient][com.swetdot.swetgram.data.api.createMediaHttpClient] -- the bare engine,
 * with no bearer token and no `/api/v1` base -- never the configured API client. See that function
 * for why sending one to `media.swetgram.com` is a leak rather than an inconvenience.
 *
 * NO CROSSFADE TRANSITION is configured, even though Coil offers one. It would run inside the
 * loader, below the composition, where `LocalReduceMotion` cannot reach it -- so §22.6 would be
 * quietly violated for every image in the app. The appearance of an image is animated where every
 * other animation in Swetgram is: in the composable, out of `SwetMotion.kt`'s tokens.
 *
 * NO DISK CACHE, deliberately. Coil 3 wires one only when given an okio `FileSystem` and a
 * platform cache directory, and neither exists in commonMain without a new expect/actual that
 * cannot be verified for iOS on this host (CLAUDE.md: framework linking and iOS tests require
 * macOS). The memory cache is on by default, so scrolling a conversation does not refetch; a cold
 * start does. That is a performance gap, not a correctness one.
 */
// `KtorNetworkFetcherFactory` is marked experimental in Coil 3.5.0 -- the network layer's own
// `NetworkClient`/`CacheStrategy` surface is still moving. Opted in HERE, at the one place that
// touches it, rather than module-wide: this is the file to revisit when the API settles, and a
// build-script-level opt-in would silence the warning for code that has not been reviewed for it.
@OptIn(ExperimentalCoilApi::class)
fun swetImageLoader(context: PlatformContext, http: HttpClient): ImageLoader =
    ImageLoader.Builder(context)
        .components { add(KtorNetworkFetcherFactory(httpClient = http)) }
        .build()
